import React from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  FieldWrapper,
  FormPreloader,
  FormTitle,
  Label,
  RegularField,
  Text,
  TextAreaField,
  useFormContext,
} from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { IRoleBasicProperties, RoleLevel } from '@/modules/role/role.types';

import AllowCreateAnotherRole, { AllowRolesDropdown } from './AllowCreateAnotherRole';
import PartnerLabel from './PartnerLabel';
import RoleLevelInput, { RolePartnerPicker } from './RoleLevelInput';

interface Props {
  isEditMode: boolean;
  isDisableLevel?: boolean;
  isLoading: boolean;
  isModalMode?: boolean;
  defaultValues?: Partial<IRoleBasicProperties>;
}
const BasicProperties: React.FC<Props> = ({
  isDisableLevel,
  isEditMode,
  defaultValues,
  isLoading,
  isModalMode,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<IRoleBasicProperties>();
  return (
    <Box round="container1Round" background="canvas" pad="l" border={{ size: 'small', color: 'border1' }}>
      <FormTitle>Basic Properties</FormTitle>
      {isLoading ? (
        <FormPreloader testId="preloader" count={4} />
      ) : (
        <Box direction="column">
          <FieldWrapper margin={{ bottom: !isEditMode ? '' : 'm', top: isModalMode ? '' : 'm' }}>
            <Label showRequireIcon={isEditMode} name="roleName" isNarrow={isModalMode}>
              Role name
            </Label>
            {isEditMode ? (
              <ControllerWrapper errorText={errors?.name?.message}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Box width={{ max: 'medium' }}>
                      <RegularField
                        testId="role_name"
                        editMode={isEditMode}
                        placeholder="Role name"
                        isError={!!errors?.name?.message}
                        onChange={field.onChange}
                        value={field.value}
                        name={field.name}
                      />
                    </Box>
                  )}
                />
              </ControllerWrapper>
            ) : (
              <Text data-testid={`role-name_${defaultValues?.name}`}>
                {defaultValues?.name || EMPTY_FIELD_VALUE}
              </Text>
            )}
          </FieldWrapper>

          <FieldWrapper align={!isEditMode ? 'center' : 'start'} margin={{ bottom: !isEditMode ? '' : 'm' }}>
            <Label showRequireIcon={isEditMode} name="level" isNarrow={isModalMode}>
              Level
            </Label>

            <ControllerWrapper errorText={errors?.level?.message}>
              <Controller
                name="level"
                control={control}
                render={({ field }) => (
                  <Box width={{ max: 'medium' }}>
                    <RoleLevelInput
                      disabled={isDisableLevel}
                      isEditMode={isEditMode}
                    
                      value={field.value}
                      onChangeValue={field.onChange}
                    >
                      <Controller
                        name="organizationId"
                        control={control}
                        render={({ field }) => (
                          
                          <RolePartnerPicker
                            errorText={errors?.organizationId?.message}
                            disabled={isDisableLevel}
                            value={field.value}
                            onChangeValue={field.onChange}
                          />
                        
                        )}
                      />
                    </RoleLevelInput>
                  </Box>
                )}
              />
            </ControllerWrapper>

          </FieldWrapper>
          {defaultValues?.level === RoleLevel.Partner && !isEditMode ? (
            <FieldWrapper margin={{ bottom: !isEditMode ? '' : 'm' }}>
              <Label name="roleName" isNarrow={isModalMode}>
                Partner
              </Label>
              <PartnerLabel id={defaultValues.organizationId} />
            </FieldWrapper>
          ) : null}

          <FieldWrapper margin={{ bottom: !isEditMode ? '' : 'm' }}>
            <Label name="roleName" isNarrow={isModalMode}>
              Description
            </Label>
            {isEditMode ? (
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Box width={{ max: 'medium', width: 'medium' }} direction="column">
                    <TextAreaField
                      data-testid="role_description_input"
                      value={field.value}
                      onChangeValue={field.onChange}
                      placeholder="Description"
                    />
                  </Box>
                )}
              />
            ) : (
              <Text size="medium" weight={400}>
                {defaultValues?.description || EMPTY_FIELD_VALUE}
              </Text>
            )}
          </FieldWrapper>

          <FieldWrapper align='top' margin={{ bottom: !isEditMode ? '' : 'm' }}>
            <Label name="roleName" isNarrow={isModalMode}>
              Allow other roles to create this role
            </Label>

            <Controller
              name="isAllowedToCreateAnotherRole"
              control={control}
              render={({ field }) => (
                <Box width={{ max: 'medium', width: 'medium' }} fill>
                  <AllowCreateAnotherRole
                    value={field.value}
                    onChangeValue={field.onChange}
                    isEditMode={isEditMode}
                  >
                    <Controller
                      name="rolesIdsAllowedForCreation"
                      control={control}
                      render={({ field }) => (
                        <AllowRolesDropdown
                          isAllowOtherMode
                          isEditMode={isEditMode}
                          onChangeValues={field.onChange}
                          values={field.value || []}
                        />
                      )}
                    />
                  </AllowCreateAnotherRole>
                </Box>
              )}
            />
          </FieldWrapper>
        </Box>
      )}
    </Box>
  );
};

export default BasicProperties;
